import type { NeckDiagram } from "./model.js";
import { dotLabel } from "./theory.js";

export function layoutDiagram(
  diagram: NeckDiagram,
  options: { leftHanded?: boolean; width?: number } = {},
) {
  const upright = diagram.kind === "chord";
  const first = Math.max(1, diagram.fretFrom);
  const count = diagram.fretTo - first + 1;
  const strings = diagram.tuning.length;
  // 48-unit spacing remains tappable at the editor's intrinsic size.
  const width =
    options.width ?? (upright ? (strings - 1) * 48 + 96 : count * 48 + 144);
  const height = upright ? count * 48 + 140 : (strings - 1) * 48 + 140;
  const xMirror = (x: number) => (options.leftHanded ? width - x : x);
  const fretStep = upright ? 48 : (width - 144) / count;
  const stringStep = upright ? (width - 96) / (strings - 1) : 48;
  const point = (string: number, fret: number) => ({
    x: xMirror(
      upright
        ? 48 + string * stringStep
        : fret === 0
          ? 44
          : 96 + (fret - first + 0.5) * fretStep,
    ),
    y: upright
      ? fret === 0
        ? 44
        : 76 + (fret - first + 0.5) * fretStep
      : 76 + (strings - 1 - string) * stringStep,
  });
  const lines: Array<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    nut?: boolean;
  }> = [];
  for (let s = 0; s < strings; s++) {
    const p = point(s, first);
    lines.push(
      upright
        ? { x1: p.x, x2: p.x, y1: 76, y2: 76 + count * fretStep }
        : { x1: xMirror(96), x2: xMirror(width - 48), y1: p.y, y2: p.y },
    );
  }
  for (let f = 0; f <= count; f++)
    lines.push(
      upright
        ? {
            x1: xMirror(48),
            x2: xMirror(width - 48),
            y1: 76 + f * fretStep,
            y2: 76 + f * fretStep,
            nut: f === 0 && first === 1,
          }
        : {
            x1: xMirror(96 + f * fretStep),
            x2: xMirror(96 + f * fretStep),
            y1: 76,
            y2: height - 64,
            nut: f === 0 && first === 1,
          },
    );
  const cells = [];
  for (let s = 0; s < strings; s++)
    for (let fret = diagram.fretFrom; fret <= diagram.fretTo; fret++)
      cells.push({ string: s, fret, ...point(s, fret) });
  return {
    width,
    height,
    point,
    lines,
    cells,
    dots: diagram.dots
      .filter((d) => d.fret >= diagram.fretFrom && d.fret <= diagram.fretTo)
      .map((dot) => ({
        ...dot,
        ...point(dot.string, dot.fret),
        label: dotLabel(diagram, dot),
      })),
    barres: diagram.barres
      .filter((b) => b.fret >= first && b.fret <= diagram.fretTo)
      .map((b) => ({
        ...b,
        from: point(b.fromString, b.fret),
        to: point(b.toString, b.fret),
      })),
    marks: diagram.stringMarks.map((mark, string) => ({
      mark,
      string,
      ...point(string, 0),
    })),
    fretLabels: Array.from({ length: count }, (_, i) => ({
      fret: first + i,
      x: upright ? xMirror(20) : point(0, first + i).x,
      y: upright ? point(0, first + i).y : height - 32,
    })),
    inlays: upright
      ? []
      : [3, 5, 7, 9, 12, 15, 17, 19, 21, 24]
          .filter((f) => f >= first && f <= diagram.fretTo)
          .flatMap((f) =>
            (f % 12 === 0 ? [-8, 8] : [0]).map((offset) => ({
              x: point(0, f).x,
              y: 76 + ((strings - 1) * stringStep) / 2 + offset,
            })),
          ),
    positionLabel: first > 1 ? `${first}fr` : "",
  };
}
